import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth, getAuthHeaders } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  ChatCircle, 
  CheckCircle,
  Spinner,
  Users,
  Calculator,
  Plus,
  PencilSimple,
  Trash,
  Prohibit,
  Package,
  EnvelopeSimple,
  Storefront,
  Image as ImageIcon,
  UploadSimple,
  FileText,
  WarningCircle
} from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function SettingsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  if (user?.role !== 'admin') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-500">Accès réservé aux administrateurs</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="text-gray-500 -ml-2 gap-2"
          >
            <ArrowLeft size={18} />
            Retour au tableau de bord
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        <h1 className="text-2xl font-heading font-bold text-gray-900 mb-8">
          Paramètres
        </h1>

        <Tabs defaultValue="users" className="space-y-6">
          <TabsList className="bg-gray-100 border border-gray-200 p-1 h-auto flex flex-wrap justify-start gap-1 w-full">
            <TabsTrigger value="users" className="text-[13px] px-2.5 py-1.5 data-[state=active]:bg-white">
              <Users size={15} className="mr-1.5" />
              Utilisateurs
            </TabsTrigger>
            <TabsTrigger value="pricing" className="text-[13px] px-2.5 py-1.5 data-[state=active]:bg-white">
              <Calculator size={15} className="mr-1.5" />
              Tarification
            </TabsTrigger>
            <TabsTrigger value="twilio" className="text-[13px] px-2.5 py-1.5 data-[state=active]:bg-white">
              <ChatCircle size={15} className="mr-1.5" />
              SMS Twilio
            </TabsTrigger>
            <TabsTrigger value="recostock" className="text-[13px] px-2.5 py-1.5 data-[state=active]:bg-white">
              <Package size={15} className="mr-1.5" />
              Reco Stock
            </TabsTrigger>
            <TabsTrigger value="sms-templates" className="text-[13px] px-2.5 py-1.5 data-[state=active]:bg-white">
              <EnvelopeSimple size={15} className="mr-1.5" />
              Modèles SMS
            </TabsTrigger>
            <TabsTrigger value="email" className="text-[13px] px-2.5 py-1.5 data-[state=active]:bg-white">
              <EnvelopeSimple size={15} className="mr-1.5" />
              Email
            </TabsTrigger>
            <TabsTrigger value="suppliers" className="text-[13px] px-2.5 py-1.5 data-[state=active]:bg-white">
              <Storefront size={15} className="mr-1.5" />
              Fournisseurs
            </TabsTrigger>
            <TabsTrigger value="google-maps" className="text-[13px] px-2.5 py-1.5 data-[state=active]:bg-white">
              <Calculator size={15} className="mr-1.5" />
              Google Maps
            </TabsTrigger>
            <TabsTrigger value="branding" className="text-[13px] px-2.5 py-1.5 data-[state=active]:bg-white">
              <ImageIcon size={15} className="mr-1.5" />
              Identité visuelle
            </TabsTrigger>
            <TabsTrigger value="invoice-counter" className="text-[13px] px-2.5 py-1.5 data-[state=active]:bg-white">
              <FileText size={15} className="mr-1.5" />
              Compteur factures
            </TabsTrigger>
          </TabsList>

          <TabsContent value="users">
            <UsersSection />
          </TabsContent>

          <TabsContent value="pricing">
            <PricingSection />
          </TabsContent>

          <TabsContent value="twilio">
            <TwilioSection />
          </TabsContent>

          <TabsContent value="recostock">
            <RecoStockSection />
          </TabsContent>

          <TabsContent value="sms-templates">
            <SmsTemplatesSection />
          </TabsContent>

          <TabsContent value="email">
            <EmailConfigSection />
          </TabsContent>

          <TabsContent value="suppliers">
            <SuppliersSection />
          </TabsContent>

          <TabsContent value="google-maps">
            <GoogleMapsSection />
          </TabsContent>

          <TabsContent value="branding">
            <BrandingSection />
          </TabsContent>

          <TabsContent value="invoice-counter">
            <InvoiceCounterSection />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

// ==================== USERS SECTION ====================
function UsersSection() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/api/users`, { headers: getAuthHeaders() });
      // Sort: active first, deactivated last
      const sorted = [...response.data].sort((a, b) => {
        if (a.active === b.active) return 0;
        return a.active ? -1 : 1;
      });
      setUsers(sorted);
    } catch (err) {
      toast.error('Erreur lors du chargement des utilisateurs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleDeactivate = async (u) => {
    try {
      await axios.post(`${API}/api/users/${u.id}/deactivate`, {}, { headers: getAuthHeaders() });
      toast.success('Utilisateur désactivé');
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const handleReactivate = async (u) => {
    try {
      await axios.post(`${API}/api/users/${u.id}/reactivate`, {}, { headers: getAuthHeaders() });
      toast.success('Utilisateur réactivé');
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/api/users/${deleteTarget.id}`, { headers: getAuthHeaders() });
      toast.success('Utilisateur supprimé définitivement');
      setDeleteTarget(null);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la suppression');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Gestion des utilisateurs</h2>
          <p className="text-sm text-gray-500">Créez et gérez les comptes Admin, Opérateur et Technicien</p>
        </div>
        <Button
          onClick={() => { setEditingUser(null); setDialogOpen(true); }}
          className="bg-reco-500 hover:bg-reco-600 text-white gap-2"
          data-testid="new-user-btn"
        >
          <Plus size={16} />
          Nouvel utilisateur
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-8">
          <Spinner size={24} className="animate-spin text-gray-400 mx-auto" />
        </div>
      ) : (
        <div className="space-y-3">
          {users.map(u => {
            const isSelf = u.id === currentUser?.id;
            return (
              <div
                key={u.id}
                className={`flex items-center justify-between p-4 rounded-lg border ${u.active ? 'bg-gray-50 border-gray-200' : 'bg-red-50 border-red-200'}`}
                data-testid={`user-row-${u.id}`}
              >
                <div>
                  <div className="font-medium text-gray-900">
                    {u.name}
                    {isSelf && <span className="ml-2 text-xs text-gray-500">(vous)</span>}
                  </div>
                  <div className="text-sm text-gray-500">{u.email}</div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                    u.role === 'admin' ? 'bg-purple-100 text-purple-700' :
                    u.role === 'operateur' ? 'bg-blue-100 text-blue-700' :
                    'bg-green-100 text-green-700'
                  }`}>
                    {u.role}
                  </span>
                  {!u.active && (
                    <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-700">
                      Désactivé
                    </span>
                  )}

                  {/* Edit (active users only, always available) */}
                  {u.active && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => { setEditingUser(u); setDialogOpen(true); }}
                      className="text-gray-400 hover:text-gray-600"
                      title="Modifier"
                      data-testid={`user-edit-${u.id}`}
                    >
                      <PencilSimple size={16} />
                    </Button>
                  )}

                  {/* Deactivate (active, not self) */}
                  {u.active && !isSelf && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDeactivate(u)}
                      className="h-8 gap-1.5 border-amber-300 text-amber-700 hover:bg-amber-50 hover:text-amber-800"
                      data-testid={`user-deactivate-${u.id}`}
                    >
                      <Prohibit size={14} />
                      Désactiver
                    </Button>
                  )}

                  {/* Reactivate (deactivated users) */}
                  {!u.active && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleReactivate(u)}
                      className="h-8 gap-1.5 border-green-300 text-green-700 hover:bg-green-50 hover:text-green-800"
                      data-testid={`user-reactivate-${u.id}`}
                    >
                      <CheckCircle size={14} />
                      Réactiver
                    </Button>
                  )}

                  {/* Permanent delete (not self) */}
                  {!isSelf && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setDeleteTarget(u)}
                      className="text-gray-400 hover:text-red-600 hover:bg-red-50"
                      title="Supprimer définitivement"
                      data-testid={`user-delete-${u.id}`}
                    >
                      <Trash size={16} />
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <UserDialog
        isOpen={dialogOpen}
        onClose={() => setDialogOpen(false)}
        user={editingUser}
        onSuccess={fetchUsers}
      />

      {/* Permanent deletion confirmation modal */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && !deleting && setDeleteTarget(null)}>
        <DialogContent className="max-w-md" data-testid="user-delete-modal">
          <DialogHeader>
            <DialogTitle className="text-red-700">Supprimer définitivement cet utilisateur ?</DialogTitle>
            <DialogDescription className="pt-2">
              Cette action est <strong>irréversible</strong>. Le compte{' '}
              <strong>{deleteTarget?.name}</strong> ({deleteTarget?.email}) sera{' '}
              <strong>définitivement supprimé</strong> de la base de données.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2 justify-end pt-4">
            <Button
              variant="outline"
              onClick={() => setDeleteTarget(null)}
              disabled={deleting}
              data-testid="user-delete-cancel"
            >
              Annuler
            </Button>
            <Button
              onClick={confirmDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 text-white gap-2"
              data-testid="user-delete-confirm"
            >
              {deleting ? <Spinner size={14} className="animate-spin" /> : <Trash size={14} />}
              Confirmer la suppression
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function UserDialog({ isOpen, onClose, user, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'operateur',
    active: true
  });

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name,
        email: user.email,
        password: '',
        role: user.role,
        active: user.active,
        phone: user.phone || ''
      });
    } else {
      setFormData({ name: '', email: '', password: '', role: 'operateur', active: true, phone: '' });
    }
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (user) {
        const data = { ...formData };
        if (!data.password) delete data.password;
        await axios.put(`${API}/api/users/${user.id}`, data, { headers: getAuthHeaders() });
        toast.success('Utilisateur mis à jour');
      } else {
        await axios.post(`${API}/api/users`, formData, { headers: getAuthHeaders() });
        toast.success('Utilisateur créé');
      }
      onSuccess();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-white">
        <DialogHeader>
          <DialogTitle>{user ? 'Modifier utilisateur' : 'Nouvel utilisateur'}</DialogTitle>
          <DialogDescription>
            {user ? 'Modifiez les informations du compte' : 'Créez un nouveau compte utilisateur'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Nom</Label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))}
              required
            />
          </div>

          <div className="space-y-2">
            <Label>Email</Label>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData(p => ({ ...p, email: e.target.value }))}
              required
            />
          </div>

          <div className="space-y-2">
            <Label>{user ? 'Nouveau mot de passe (laisser vide pour ne pas changer)' : 'Mot de passe'}</Label>
            <Input
              type="password"
              value={formData.password}
              onChange={(e) => setFormData(p => ({ ...p, password: e.target.value }))}
              required={!user}
            />
          </div>

          <div className="space-y-2">
            <Label>Rôle</Label>
            <Select value={formData.role} onValueChange={(v) => setFormData(p => ({ ...p, role: v }))}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="admin">Admin - Accès complet</SelectItem>
                <SelectItem value="operateur">Opérateur - Dashboard uniquement</SelectItem>
                <SelectItem value="technician">Technicien - Application mobile</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-gray-500">
              L'opérateur n'a pas accès aux paramètres de tarification et au CA.
            </p>
          </div>

          {formData.role === 'technician' && (
            <div className="space-y-2">
              <Label>Numéro de téléphone</Label>
              <Input
                type="tel"
                value={formData.phone || ''}
                onChange={(e) => setFormData(p => ({ ...p, phone: e.target.value }))}
                placeholder="+33 6 12 34 56 78"
              />
              <p className="text-xs text-gray-500">
                Ce numéro sera communiqué au client par SMS quand le technicien est en route.
              </p>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>Annuler</Button>
            <Button type="submit" disabled={loading} className="bg-reco-500 hover:bg-reco-600 text-white">
              {loading ? 'Enregistrement...' : user ? 'Mettre à jour' : 'Créer'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ==================== PRICING SECTION ====================
function PricingSection() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('remplacement');
  const [openSections, setOpenSections] = useState({});

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await axios.get(`${API}/api/settings/pricing`, { headers: getAuthHeaders() });
        setConfig(response.data);
      } catch (err) {
        toast.error('Erreur lors du chargement');
      } finally {
        setLoading(false);
      }
    };
    fetchConfig();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/settings/pricing`, config, { headers: getAuthHeaders() });
      toast.success('Configuration enregistrée');
    } catch (err) {
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field, value) => {
    setConfig(prev => ({ ...prev, [field]: parseFloat(value) || 0 }));
  };

  const toggleSection = (key) => {
    setOpenSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  if (loading) {
    return <div className="text-center py-8"><Spinner size={24} className="animate-spin text-gray-400 mx-auto" /></div>;
  }

  return (
    <div className="space-y-6">
      {/* General */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="font-semibold text-gray-900 mb-4">Configuration générale</h3>
        <div className="space-y-2">
          <Label>Point de départ par défaut</Label>
          <Input
            value={config?.departure_point || ''}
            onChange={(e) => setConfig(prev => ({ ...prev, departure_point: e.target.value }))}
            placeholder="Mandelieu, France"
          />
        </div>
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-full p-1 border border-gray-200">
        <button
          onClick={() => setActiveTab('remplacement')}
          className={`flex-1 px-4 py-2 text-sm font-medium rounded-full transition-colors ${
            activeTab === 'remplacement' ? 'bg-reco-500 text-white' : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Remplacement pneus
        </button>
        <button
          onClick={() => setActiveTab('reparation')}
          className={`flex-1 px-4 py-2 text-sm font-medium rounded-full transition-colors ${
            activeTab === 'reparation' ? 'bg-reco-500 text-white' : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Réparation crevaison
        </button>
      </div>

      {activeTab === 'remplacement' && (
        <div className="space-y-3">
          <CollapsibleSection title="Tarif pneus" icon="📦" open={openSections['r_pneus'] !== false} onToggle={() => toggleSection('r_pneus')}>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <PricingField label="Prix de base (€)" value={config?.replace_base_price} onChange={(v) => updateField('replace_base_price', v)} />
              <PricingField label="Diamètre 17-18 (€)" value={config?.replace_diameter_17_18} onChange={(v) => updateField('replace_diameter_17_18', v)} />
              <PricingField label="Diamètre 19-20 (€)" value={config?.replace_diameter_19_20} onChange={(v) => updateField('replace_diameter_19_20', v)} />
              <PricingField label="Diamètre 21 (€)" value={config?.replace_diameter_21} onChange={(v) => updateField('replace_diameter_21', v)} />
              <PricingField label="Diamètre 22-23 (€)" value={config?.replace_diameter_22_23} onChange={(v) => updateField('replace_diameter_22_23', v)} />
              <PricingField label="Multiplicateur Neuf (x)" value={config?.replace_type_neuf_multiplier} onChange={(v) => updateField('replace_type_neuf_multiplier', v)} step="0.1" />
              <PricingField label="Multiplicateur Premium (x)" value={config?.replace_premium_multiplier} onChange={(v) => updateField('replace_premium_multiplier', v)} step="0.1" />
              <PricingField label="État 1/4 (x)" value={config?.replace_condition_1_multiplier} onChange={(v) => updateField('replace_condition_1_multiplier', v)} step="0.1" />
              <PricingField label="État 2/4 (x)" value={config?.replace_condition_2_multiplier} onChange={(v) => updateField('replace_condition_2_multiplier', v)} step="0.1" />
              <PricingField label="État 3/4 (x)" value={config?.replace_condition_3_multiplier} onChange={(v) => updateField('replace_condition_3_multiplier', v)} step="0.1" />
              <PricingField label="État 4/4 (x)" value={config?.replace_condition_4_multiplier} onChange={(v) => updateField('replace_condition_4_multiplier', v)} step="0.1" />
              <PricingField label="Taille rare (x)" value={config?.replace_rare_size_multiplier} onChange={(v) => updateField('replace_rare_size_multiplier', v)} step="0.1" />
              <PricingField label="Remise 1 pneu (€)" value={config?.replace_single_tire_discount} onChange={(v) => updateField('replace_single_tire_discount', v)} />
            </div>
          </CollapsibleSection>

          <CollapsibleSection title="Tarif montage" icon="🔧" open={openSections['r_montage'] !== false} onToggle={() => toggleSection('r_montage')}>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <PricingField label="Prix de base montage (€)" value={config?.replace_montage_base} onChange={(v) => updateField('replace_montage_base', v)} />
              <PricingField label="Montage 17-18 (€)" value={config?.replace_montage_17_18} onChange={(v) => updateField('replace_montage_17_18', v)} />
              <PricingField label="Montage 19-20 (€)" value={config?.replace_montage_19_20} onChange={(v) => updateField('replace_montage_19_20', v)} />
              <PricingField label="Montage 21 (€)" value={config?.replace_montage_21} onChange={(v) => updateField('replace_montage_21', v)} />
              <PricingField label="Montage 22-23 (€)" value={config?.replace_montage_22_23} onChange={(v) => updateField('replace_montage_22_23', v)} />
            </div>
          </CollapsibleSection>

          <CollapsibleSection title="Tarif déplacement" icon="🚗" open={openSections['r_deplacement'] !== false} onToggle={() => toggleSection('r_deplacement')}>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <PricingField label="Trajet < 30min (€)" value={config?.replace_travel_under_30} onChange={(v) => updateField('replace_travel_under_30', v)} />
              <PricingField label="Trajet 31-45min (€)" value={config?.replace_travel_31_45} onChange={(v) => updateField('replace_travel_31_45', v)} />
              <PricingField label="Trajet 46-75min (€)" value={config?.replace_travel_46_75} onChange={(v) => updateField('replace_travel_46_75', v)} />
              <PricingField label="Trajet 76-120min (€)" value={config?.replace_travel_76_120} onChange={(v) => updateField('replace_travel_76_120', v)} />
            </div>
          </CollapsibleSection>

          <CollapsibleSection title="Tarif extras" icon="⭐" open={openSections['r_extras'] !== false} onToggle={() => toggleSection('r_extras')}>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <PricingField label="Urgence 2h (€)" value={config?.replace_urgence_2h} onChange={(v) => updateField('replace_urgence_2h', v)} />
              <PricingField label="Urgence 8h (€)" value={config?.replace_urgence_8h} onChange={(v) => updateField('replace_urgence_8h', v)} />
              <PricingField label="Autre jour (€)" value={config?.replace_autre_jour} onChange={(v) => updateField('replace_autre_jour', v)} />
              <PricingField label="Haute saison (€)" value={config?.replace_high_season} onChange={(v) => updateField('replace_high_season', v)} />
            </div>
          </CollapsibleSection>
        </div>
      )}

      {activeTab === 'reparation' && (
        <div className="space-y-3">
          <CollapsibleSection title="Tarif réparation" icon="🔧" open={openSections['rep_base'] !== false} onToggle={() => toggleSection('rep_base')}>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <PricingField label="Prix de base (€)" value={config?.repair_base_price} onChange={(v) => updateField('repair_base_price', v)} />
            </div>
          </CollapsibleSection>

          <CollapsibleSection title="Tarif déplacement" icon="🚗" open={openSections['rep_deplacement'] !== false} onToggle={() => toggleSection('rep_deplacement')}>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <PricingField label="Trajet < 30min (€)" value={config?.repair_travel_under_30} onChange={(v) => updateField('repair_travel_under_30', v)} />
              <PricingField label="Trajet 31-45min (€)" value={config?.repair_travel_31_45} onChange={(v) => updateField('repair_travel_31_45', v)} />
              <PricingField label="Trajet 46-75min (€)" value={config?.repair_travel_46_75} onChange={(v) => updateField('repair_travel_46_75', v)} />
              <PricingField label="Trajet 76-120min (€)" value={config?.repair_travel_76_120} onChange={(v) => updateField('repair_travel_76_120', v)} />
            </div>
          </CollapsibleSection>

          <CollapsibleSection title="Tarif extras" icon="⭐" open={openSections['rep_extras'] !== false} onToggle={() => toggleSection('rep_extras')}>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <PricingField label="Urgence 2h (€)" value={config?.repair_urgence_2h} onChange={(v) => updateField('repair_urgence_2h', v)} />
              <PricingField label="Urgence 8h (€)" value={config?.repair_urgence_8h} onChange={(v) => updateField('repair_urgence_8h', v)} />
              <PricingField label="Autre jour (€)" value={config?.repair_autre_jour} onChange={(v) => updateField('repair_autre_jour', v)} />
              <PricingField label="Haute saison (€)" value={config?.repair_high_season} onChange={(v) => updateField('repair_high_season', v)} />
            </div>
          </CollapsibleSection>
        </div>
      )}

      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving} className="bg-reco-500 hover:bg-reco-600 text-white">
          {saving ? 'Enregistrement...' : 'Enregistrer la configuration'}
        </Button>
      </div>
    </div>
  );
}

function CollapsibleSection({ title, icon, open, onToggle, children }) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
      >
        <span className="font-semibold text-gray-900 flex items-center gap-2">
          <span>{icon}</span> {title}
        </span>
        <span className={`text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`}>&#9662;</span>
      </button>
      {open && <div className="px-4 pb-4">{children}</div>}
    </div>
  );
}

function PricingField({ label, value, onChange, step = "1" }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs text-gray-500">{label}</Label>
      <Input
        type="number"
        step={step}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        className="bg-white"
      />
    </div>
  );
}

// ==================== TWILIO SECTION ====================
function TwilioSection() {
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testPhone, setTestPhone] = useState('');
  
  const [config, setConfig] = useState({
    account_sid: '',
    auth_token: '',
    sender_phone: '',
    configured: false
  });

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await axios.get(`${API}/api/settings/twilio`, { headers: getAuthHeaders() });
        if (response.data.configured) {
          setConfig({ ...response.data, auth_token: '' });
        }
      } catch (err) {
        // Ignore
      }
    };
    fetchConfig();
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/api/settings/twilio`, {
        account_sid: config.account_sid,
        auth_token: config.auth_token,
        sender_phone: config.sender_phone
      }, { headers: getAuthHeaders() });
      toast.success('Configuration Twilio enregistrée');
      setConfig(prev => ({ ...prev, configured: true }));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    } finally {
      setLoading(false);
    }
  };

  const handleTest = async () => {
    if (!testPhone) {
      toast.error('Veuillez entrer un numéro de téléphone');
      return;
    }
    setTesting(true);
    try {
      await axios.post(`${API}/api/settings/twilio/test?phone=${encodeURIComponent(testPhone)}`, {}, { headers: getAuthHeaders() });
      toast.success('SMS de test envoyé !');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de l\'envoi du SMS');
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6">
      <div className="flex items-center gap-3 mb-6">
        <ChatCircle size={24} className="text-reco-500" />
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Configuration Twilio SMS</h2>
          <p className="text-sm text-gray-500">Configurez Twilio pour envoyer des SMS automatiques aux clients</p>
        </div>
        {config.configured && <CheckCircle size={20} className="text-green-500 ml-auto" />}
      </div>

      <form onSubmit={handleSave} className="space-y-4">
        <div className="space-y-2">
          <Label>Account SID</Label>
          <Input
            value={config.account_sid}
            onChange={(e) => setConfig(prev => ({ ...prev, account_sid: e.target.value }))}
            placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
            className="font-mono"
          />
        </div>

        <div className="space-y-2">
          <Label>Auth Token</Label>
          <Input
            type="password"
            value={config.auth_token}
            onChange={(e) => setConfig(prev => ({ ...prev, auth_token: e.target.value }))}
            placeholder={config.configured ? '••••••••••••••••' : 'Votre Auth Token'}
          />
        </div>

        <div className="space-y-2">
          <Label>Expéditeur (numéro ou nom alphanumérique)</Label>
          <Input
            value={config.sender_phone}
            onChange={(e) => setConfig(prev => ({ ...prev, sender_phone: e.target.value }))}
            placeholder="RECOPNEUS ou +33612345678"
          />
          <p className="text-xs text-gray-500">
            Vous pouvez utiliser un nom alphanumérique (ex: RECOPNEUS, max 11 caractères) ou un numéro au format international (+33...).
          </p>
        </div>

        <Button type="submit" disabled={loading} className="bg-reco-500 hover:bg-reco-600 text-white">
          {loading ? 'Enregistrement...' : 'Enregistrer'}
        </Button>
      </form>

      {config.configured && (
        <div className="mt-6 pt-6 border-t border-gray-200">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Tester la configuration</h3>
          <div className="flex gap-2">
            <Input
              value={testPhone}
              onChange={(e) => setTestPhone(e.target.value)}
              placeholder="+33612345678"
              className="flex-1"
            />
            <Button onClick={handleTest} disabled={testing} variant="outline">
              {testing ? <Spinner size={18} className="animate-spin" /> : 'Tester'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ==================== RECO STOCK SECTION ====================
function RecoStockSection() {
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  
  const [config, setConfig] = useState({
    base_url: 'https://stock.recopneus.com',
    email: '',
    password: '',
    configured: false
  });

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await axios.get(`${API}/api/settings/reco-stock`, { headers: getAuthHeaders() });
        if (response.data.configured) {
          setConfig({ ...response.data, password: '' });
        }
      } catch (err) {
        // Ignore
      }
    };
    fetchConfig();
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/api/settings/reco-stock`, {
        base_url: config.base_url,
        email: config.email,
        password: config.password
      }, { headers: getAuthHeaders() });
      toast.success('Configuration Reco Stock enregistrée');
      setConfig(prev => ({ ...prev, configured: true }));
      setTestResult(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    } finally {
      setLoading(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const response = await axios.post(`${API}/api/settings/reco-stock/test`, {}, { headers: getAuthHeaders() });
      setTestResult(response.data);
      if (response.data.success) {
        toast.success('Connexion Reco Stock réussie !');
      } else {
        toast.error(response.data.message);
      }
    } catch (err) {
      setTestResult({ success: false, message: err.response?.data?.detail || 'Erreur de test' });
      toast.error(err.response?.data?.detail || 'Erreur lors du test');
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6">
      <div className="flex items-center gap-3 mb-6">
        <Package size={24} className="text-reco-500" />
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Connexion Reco Stock</h2>
          <p className="text-sm text-gray-500">Configurez l'accès à l'API Reco Stock pour gérer les sorties de stock</p>
        </div>
        {config.configured && <CheckCircle size={20} className="text-green-500 ml-auto" />}
      </div>

      <form onSubmit={handleSave} className="space-y-4">
        <div className="space-y-2">
          <Label>URL Reco Stock</Label>
          <Input
            value={config.base_url}
            onChange={(e) => setConfig(prev => ({ ...prev, base_url: e.target.value }))}
            placeholder="https://stock.recopneus.com"
            className="font-mono"
          />
        </div>

        <div className="space-y-2">
          <Label>Email admin Reco Stock</Label>
          <Input
            type="email"
            value={config.email}
            onChange={(e) => setConfig(prev => ({ ...prev, email: e.target.value }))}
            placeholder="admin@recopneus.com"
          />
        </div>

        <div className="space-y-2">
          <Label>Mot de passe</Label>
          <Input
            type="password"
            value={config.password}
            onChange={(e) => setConfig(prev => ({ ...prev, password: e.target.value }))}
            placeholder={config.configured ? '••••••••••••••••' : 'Mot de passe Reco Stock'}
          />
        </div>

        <Button type="submit" disabled={loading} className="bg-reco-500 hover:bg-reco-600 text-white">
          {loading ? 'Enregistrement...' : 'Enregistrer'}
        </Button>
      </form>

      {config.configured && (
        <div className="mt-6 pt-6 border-t border-gray-200">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Tester la connexion</h3>
          <Button onClick={handleTest} disabled={testing} variant="outline" className="gap-2">
            {testing ? <Spinner size={18} className="animate-spin" /> : <Package size={16} />}
            Tester la connexion
          </Button>
          {testResult && (
            <div className={`mt-3 p-3 rounded-lg text-sm ${testResult.success ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
              {testResult.message}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ==================== SMS TEMPLATES SECTION ====================
const SMS_TEMPLATE_CONFIG = [
  {
    key: 'validation',
    label: 'SMS Validation',
    description: 'Envoyé au client quand l\'intervention est validée',
    placeholders: ['{temps_estime}']
  },
  {
    key: 'depart',
    label: 'SMS Départ technicien',
    description: 'Envoyé au client quand le technicien part en route',
    placeholders: ['{telephone_technicien}', '{temps_estime}']
  },
  {
    key: 'encaissement',
    label: 'SMS Encaissement',
    description: 'Envoyé au client après l\'encaissement',
    placeholders: ['{lien_documents}']
  },
  {
    key: 'geolocalisation',
    label: 'SMS Géolocalisation',
    description: 'Envoyé au client pour demander sa position GPS',
    placeholders: ['{lien_geoloc}']
  }
];

function SmsTemplatesSection() {
  const [templates, setTemplates] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const response = await axios.get(`${API}/api/settings/sms-templates`, { headers: getAuthHeaders() });
        setTemplates(response.data);
      } catch (err) {
        toast.error('Erreur chargement des modèles SMS');
      } finally {
        setLoading(false);
      }
    };
    fetchTemplates();
  }, []);

  const handleSave = async (key) => {
    setSaving(prev => ({ ...prev, [key]: true }));
    try {
      await axios.put(`${API}/api/settings/sms-templates`, { [key]: templates[key] }, { headers: getAuthHeaders() });
      toast.success('Modèle SMS enregistré');
    } catch (err) {
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setSaving(prev => ({ ...prev, [key]: false }));
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner size={28} className="animate-spin text-reco-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {SMS_TEMPLATE_CONFIG.map(({ key, label, description, placeholders }) => (
        <div key={key} className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold text-gray-900">{label}</h3>
          </div>
          <p className="text-sm text-gray-500 mb-3">{description}</p>
          <Textarea
            value={templates[key] || ''}
            onChange={(e) => setTemplates(prev => ({ ...prev, [key]: e.target.value }))}
            className="bg-white border-gray-300 min-h-[100px] font-mono text-sm"
            data-testid={`sms-template-${key}`}
          />
          <div className="flex items-center justify-between mt-3">
            <p className="text-xs text-gray-400">
              Variables disponibles : {placeholders.map(p => <code key={p} className="bg-gray-100 px-1.5 py-0.5 rounded text-gray-600 mx-0.5">{p}</code>)}
            </p>
            <Button
              onClick={() => handleSave(key)}
              disabled={saving[key]}
              className="bg-reco-500 hover:bg-reco-600 text-white"
              data-testid={`sms-template-save-${key}`}
            >
              {saving[key] ? 'Enregistrement...' : 'Sauvegarder'}
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
}

// ==================== EMAIL CONFIG SECTION ====================
function EmailConfigSection() {
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [config, setConfig] = useState({
    smtp_server: 'smtp.gmail.com',
    smtp_port: 587,
    sender_email: '',
    sender_name: 'Reco Pneus',
    password: '',
    configured: false
  });

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await axios.get(`${API}/api/settings/email`, { headers: getAuthHeaders() });
        if (response.data.configured) {
          setConfig({ ...response.data, password: '' });
        }
      } catch {}
    };
    fetchConfig();
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/api/settings/email`, config, { headers: getAuthHeaders() });
      toast.success('Configuration email enregistrée');
      setConfig(prev => ({ ...prev, configured: true }));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setLoading(false);
    }
  };

  const handleTest = async () => {
    if (!testEmail) return;
    setTesting(true);
    setTestResult(null);
    try {
      const response = await axios.post(`${API}/api/settings/email/test`, { email: testEmail }, { headers: getAuthHeaders() });
      setTestResult(response.data);
      if (response.data.success) toast.success('Email de test envoyé');
      else toast.error(response.data.message);
    } catch (err) {
      setTestResult({ success: false, message: err.response?.data?.detail || 'Erreur test' });
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6">
      <div className="flex items-center gap-3 mb-6">
        <EnvelopeSimple size={24} className="text-reco-500" />
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Configuration Email</h2>
          <p className="text-sm text-gray-500">Pour l'envoi automatique des factures par email</p>
        </div>
        {config.configured && <CheckCircle size={20} className="text-green-500 ml-auto" />}
      </div>

      <form onSubmit={handleSave} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Serveur SMTP</Label>
            <Input value={config.smtp_server} onChange={(e) => setConfig(p => ({ ...p, smtp_server: e.target.value }))} placeholder="smtp.gmail.com" className="font-mono" />
          </div>
          <div className="space-y-2">
            <Label>Port SMTP</Label>
            <Input type="number" value={config.smtp_port} onChange={(e) => setConfig(p => ({ ...p, smtp_port: parseInt(e.target.value) || 587 }))} />
          </div>
        </div>
        <div className="space-y-2">
          <Label>Email expéditeur</Label>
          <Input type="email" value={config.sender_email} onChange={(e) => setConfig(p => ({ ...p, sender_email: e.target.value }))} placeholder="recopneus@gmail.com" />
        </div>
        <div className="space-y-2">
          <Label>Nom d'affichage</Label>
          <Input value={config.sender_name} onChange={(e) => setConfig(p => ({ ...p, sender_name: e.target.value }))} placeholder="Reco Pneus" />
        </div>
        <div className="space-y-2">
          <Label>Mot de passe / App Password</Label>
          <Input type="password" value={config.password} onChange={(e) => setConfig(p => ({ ...p, password: e.target.value }))} placeholder={config.configured ? '••••••••' : 'Mot de passe d\'application Gmail'} />
          <p className="text-xs text-gray-500">Pour Gmail, utilisez un "Mot de passe d'application" (Paramètres Google → Sécurité → Mots de passe des applications)</p>
        </div>
        <Button type="submit" disabled={loading} className="bg-reco-500 hover:bg-reco-600 text-white">
          {loading ? 'Enregistrement...' : 'Enregistrer'}
        </Button>
      </form>

      {config.configured && (
        <div className="mt-6 pt-6 border-t border-gray-200">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Tester l'envoi</h3>
          <div className="flex gap-2">
            <Input value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="email@test.com" className="flex-1" />
            <Button onClick={handleTest} disabled={testing || !testEmail} variant="outline" className="gap-2">
              {testing ? <Spinner size={16} className="animate-spin" /> : <EnvelopeSimple size={16} />}
              Tester
            </Button>
          </div>
          {testResult && (
            <div className={`mt-3 p-3 rounded-lg text-sm ${testResult.success ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
              {testResult.message}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ==================== SUPPLIERS SECTION ====================
function SuppliersSection() {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editAddress, setEditAddress] = useState('');

  const fetchSuppliers = async () => {
    try {
      const r = await axios.get(`${API}/api/suppliers`, { headers: getAuthHeaders() });
      setSuppliers(r.data);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { fetchSuppliers(); }, []);

  const handleAdd = async () => {
    if (!name.trim()) return;
    try {
      await axios.post(`${API}/api/suppliers`, { name: name.trim(), address: address.trim() }, { headers: getAuthHeaders() });
      toast.success('Fournisseur ajouté');
      setName(''); setAddress('');
      fetchSuppliers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const handleUpdate = async (id) => {
    try {
      await axios.put(`${API}/api/suppliers/${id}`, { name: editName.trim(), address: editAddress.trim() }, { headers: getAuthHeaders() });
      toast.success('Fournisseur mis à jour');
      setEditingId(null);
      fetchSuppliers();
    } catch { toast.error('Erreur'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce fournisseur ?')) return;
    try {
      await axios.delete(`${API}/api/suppliers/${id}`, { headers: getAuthHeaders() });
      toast.success('Fournisseur supprimé');
      fetchSuppliers();
    } catch { toast.error('Erreur'); }
  };

  if (loading) {
    return <div className="text-center py-8"><Spinner size={24} className="animate-spin text-gray-400 mx-auto" /></div>;
  }

  return (
    <div className="space-y-6">
      {/* Add form */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Storefront size={20} className="text-reco-500" />
          Ajouter un fournisseur
        </h3>
        <div className="flex gap-3">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Nom du fournisseur *" className="flex-1" data-testid="supplier-name-input" />
          <Input value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Adresse (optionnel)" className="flex-1" />
          <Button onClick={handleAdd} disabled={!name.trim()} className="bg-reco-500 hover:bg-reco-600 text-white gap-2" data-testid="add-supplier-btn">
            <Plus size={16} /> Ajouter
          </Button>
        </div>
      </div>

      {/* List */}
      <div className="space-y-2">
        {suppliers.length === 0 ? (
          <div className="text-center py-8 bg-white border border-gray-200 rounded-lg text-gray-500">
            Aucun fournisseur enregistré
          </div>
        ) : (
          suppliers.map(s => (
            <div key={s.id} className="bg-white border border-gray-200 rounded-lg p-4 flex items-center justify-between gap-4" data-testid={`supplier-${s.id}`}>
              {editingId === s.id ? (
                <div className="flex gap-2 flex-1">
                  <Input value={editName} onChange={(e) => setEditName(e.target.value)} className="flex-1" />
                  <Input value={editAddress} onChange={(e) => setEditAddress(e.target.value)} className="flex-1" />
                  <Button onClick={() => handleUpdate(s.id)} className="bg-reco-500 hover:bg-reco-600 text-white">OK</Button>
                  <Button variant="ghost" onClick={() => setEditingId(null)}>Annuler</Button>
                </div>
              ) : (
                <>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-900">{s.name}</div>
                    {s.address && <div className="text-sm text-gray-500">{s.address}</div>}
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => { setEditingId(s.id); setEditName(s.name); setEditAddress(s.address || ''); }} className="text-gray-400 hover:text-reco-500">
                      <PencilSimple size={16} />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(s.id)} className="text-gray-400 hover:text-red-500">
                      <Trash size={16} />
                    </Button>
                  </div>
                </>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}


// ==================== GOOGLE MAPS SECTION ====================
function GoogleMapsSection() {
  const [apiKey, setApiKey] = useState('AIzaSyBf2OTL120H0bHMSWfKTP1dpyysPrQPpm8');
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [configured, setConfigured] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      try {
        const r = await axios.get(`${API}/api/settings/google-maps`, { headers: getAuthHeaders() });
        setConfigured(r.data.configured);
      } catch {}
    };
    fetch();
  }, []);

  const handleSave = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/api/settings/google-maps`, { api_key: apiKey }, { headers: getAuthHeaders() });
      toast.success('Configuration Google Maps enregistrée');
      setConfigured(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally { setLoading(false); }
  };

  const handleTest = async () => {
    setTesting(true); setTestResult(null);
    try {
      const r = await axios.post(`${API}/api/settings/google-maps/test`, {}, { headers: getAuthHeaders() });
      setTestResult(r.data);
      r.data.success ? toast.success(r.data.message) : toast.error(r.data.message);
    } catch (err) {
      setTestResult({ success: false, message: err.response?.data?.detail || 'Erreur' });
    } finally { setTesting(false); }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6">
      <div className="flex items-center gap-3 mb-6">
        <Calculator size={24} className="text-reco-500" />
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Google Distance Matrix</h2>
          <p className="text-sm text-gray-500">Calcul des temps de trajet en temps réel</p>
        </div>
        {configured && <CheckCircle size={20} className="text-green-500 ml-auto" />}
      </div>
      <div className="space-y-4">
        <div className="space-y-2">
          <Label>Clé API Google Maps</Label>
          <Input type="password" value={apiKey} onChange={(e) => setApiKey(e.target.value)} className="font-mono" />
        </div>
        <div className="flex gap-2">
          <Button onClick={handleSave} disabled={loading} className="bg-reco-500 hover:bg-reco-600 text-white">
            {loading ? 'Enregistrement...' : 'Enregistrer'}
          </Button>
          {configured && (
            <Button onClick={handleTest} disabled={testing} variant="outline">
              {testing ? <Spinner size={16} className="animate-spin" /> : 'Tester'}
            </Button>
          )}
        </div>
        {testResult && (
          <div className={`p-3 rounded-lg text-sm ${testResult.success ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
            {testResult.message}
          </div>
        )}
      </div>
    </div>
  );
}


// ==================== BRANDING SECTION ====================
function BrandingSection() {
  const [info, setInfo] = useState({ has_logo: false });
  const [uploading, setUploading] = useState(false);
  const [bust, setBust] = useState(Date.now());

  const fetchInfo = async () => {
    try {
      const res = await axios.get(`${API}/api/settings/logo`, { headers: getAuthHeaders() });
      setInfo(res.data);
    } catch (e) {
      // silent
    }
  };

  useEffect(() => { fetchInfo(); }, []);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!['image/png', 'image/jpeg', 'image/jpg'].includes(file.type)) {
      toast.error('Format non supporté — PNG ou JPG uniquement');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Fichier trop volumineux (max 2 MB)');
      return;
    }
    const fd = new FormData();
    fd.append('file', file);
    setUploading(true);
    try {
      await axios.post(`${API}/api/settings/logo`, fd, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Logo téléchargé');
      setBust(Date.now());
      await fetchInfo();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors du téléchargement');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const onDelete = async () => {
    if (!window.confirm('Supprimer le logo ?')) return;
    try {
      await axios.delete(`${API}/api/settings/logo`, { headers: getAuthHeaders() });
      toast.success('Logo supprimé');
      setBust(Date.now());
      await fetchInfo();
    } catch (err) {
      toast.error('Erreur lors de la suppression');
    }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6 max-w-2xl">
      <h2 className="text-lg font-semibold text-gray-900 mb-2">Identité visuelle</h2>
      <p className="text-sm text-gray-500 mb-6">
        Logo affiché en haut à gauche de vos factures PDF. Formats acceptés : PNG, JPG. Taille max : 2 MB.
      </p>

      <div className="flex items-start gap-6">
        <div className="w-40 h-28 border border-gray-200 rounded bg-gray-50 flex items-center justify-center overflow-hidden">
          {info.has_logo ? (
            <img
              src={`${API}/api/settings/logo/image?ext=${info.ext}&t=${bust}`}
              alt="Logo"
              className="max-w-full max-h-full object-contain"
              data-testid="logo-preview"
            />
          ) : (
            <span className="text-sm text-gray-400">Aucun logo</span>
          )}
        </div>

        <div className="flex-1 space-y-3">
          <label className="inline-flex items-center gap-2 px-4 py-2 rounded border border-reco-500 text-reco-600 hover:bg-reco-50 cursor-pointer text-sm font-medium transition-colors">
            <UploadSimple size={16} />
            {info.has_logo ? 'Remplacer le logo' : 'Télécharger un logo'}
            <input
              type="file"
              accept="image/png, image/jpeg"
              onChange={onFile}
              disabled={uploading}
              className="hidden"
              data-testid="logo-upload-input"
            />
          </label>
          {uploading && <p className="text-xs text-gray-500">Téléchargement en cours…</p>}
          {info.has_logo && !uploading && (
            <button
              type="button"
              onClick={onDelete}
              className="block text-xs text-red-600 hover:text-red-700"
              data-testid="logo-delete-btn"
            >
              Supprimer le logo
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ==================== INVOICE COUNTER SECTION ====================
function InvoiceCounterSection() {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [nextNumber, setNextNumber] = useState('');

  const fetchState = async () => {
    try {
      const res = await axios.get(`${API}/api/settings/invoice-counter`, { headers: getAuthHeaders() });
      setState(res.data);
      setNextNumber(String(res.data.next_seq));
    } catch (err) {
      toast.error('Erreur lors du chargement du compteur');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchState(); }, []);

  const handleSave = async () => {
    const n = parseInt(nextNumber, 10);
    if (!n || n <= 0) {
      toast.error('Numéro invalide');
      return;
    }
    setSaving(true);
    try {
      const res = await axios.put(`${API}/api/settings/invoice-counter`,
        { next_number: n }, { headers: getAuthHeaders() });
      setState(res.data);
      setNextNumber(String(res.data.next_seq));
      toast.success(`Prochain N° défini : ${res.data.next_number}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-center py-8"><Spinner size={24} className="animate-spin text-gray-400 mx-auto" /></div>;
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <FileText size={18} className="text-reco-600" />
          Compteur de facturation
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          Ce compteur est <strong>partagé</strong> entre le flux automatique (lien client autonome) et
          le générateur manuel. Format des numéros : <code className="text-reco-600">F-{state?.year}-XXXX</code>.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <div className="text-xs uppercase tracking-wide text-gray-500 font-medium">Prochain N° à émettre</div>
          <div className="text-2xl font-bold text-gray-900 mt-1" data-testid="counter-next-number">
            {state?.next_number}
          </div>
        </div>
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <div className="text-xs uppercase tracking-wide text-gray-500 font-medium">Dernière séquence naturelle {state?.year}</div>
          <div className="text-2xl font-bold text-gray-900 mt-1">
            {state?.natural_max_seq > 0 ? `#${state.natural_max_seq}` : '—'}
          </div>
        </div>
      </div>

      <div className="border border-gray-200 rounded-lg p-4 space-y-3">
        <div>
          <Label className="text-sm font-medium">Définir le prochain numéro</Label>
          <p className="text-xs text-gray-500 mt-0.5">
            La très prochaine facture émise (automatique OU manuelle) portera ce numéro.
          </p>
        </div>
        <div className="flex items-center gap-2 max-w-md">
          <div className="text-sm text-gray-500 whitespace-nowrap">F-{state?.year}-</div>
          <Input
            type="number"
            min="1"
            step="1"
            value={nextNumber}
            onChange={(e) => setNextNumber(e.target.value)}
            className="max-w-[140px]"
            data-testid="counter-input"
          />
          <Button
            onClick={handleSave}
            disabled={saving || !nextNumber}
            className="bg-reco-500 hover:bg-reco-600 text-white gap-2"
            data-testid="counter-save"
          >
            {saving ? <Spinner size={14} className="animate-spin" /> : <CheckCircle size={14} />}
            Enregistrer
          </Button>
        </div>
        <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-md text-xs text-amber-800">
          <WarningCircle size={16} className="flex-shrink-0 mt-0.5" weight="fill" />
          <div>
            <strong>Attention :</strong> modifier ce compteur n&apos;affecte que les factures à venir. Les factures
            déjà émises ne sont jamais modifiées. Le nouveau numéro doit être <strong>strictement supérieur</strong> à
            toute séquence naturelle déjà utilisée cette année, sous peine d&apos;être refusé (règles de numérotation
            française : pas de trou, pas de doublon).
          </div>
        </div>
      </div>
    </div>
  );
}

